/**
 * ApiTokensPage — W2.4
 *
 * Route /developer/api-tokens
 * Premium-Guard: HTTP 402 → PremiumRequired
 * Liste aktiver Tokens + Loading/Error/Empty-States
 * Create-Modal + Rotate-Modal-Flow
 */
import { useState } from 'react'
import { Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useApiTokens } from './useApiTokens'
import { ApiQuotaCard } from './ApiQuotaCard'
import { ApiTokenRow } from './ApiTokenRow'
import { CreateTokenModal } from './CreateTokenModal'
import { TokenRevealBox } from './TokenRevealBox'
import { PremiumRequired } from '../../components/PremiumRequired'
import { DemoGuardedButton } from '../../components/DemoGuardedButton'
import type { ApiTokenWithSecret } from './useApiTokens'

// --- Skeleton ---

function SkeletonRow() {
  return (
    <div className="list-row skeleton" aria-hidden="true">
      <div className="identity-meta" style={{ flex: 1 }}>
        <div
          style={{
            width: 140,
            height: 14,
            background: 'color-mix(in srgb, var(--text) 10%, transparent)',
            borderRadius: 4,
            animation: 'ht-skeleton-pulse 1.5s ease-in-out infinite',
          }}
        />
        <div
          style={{
            width: 100,
            height: 12,
            marginTop: 4,
            background: 'color-mix(in srgb, var(--text) 8%, transparent)',
            borderRadius: 4,
            animation: 'ht-skeleton-pulse 1.5s ease-in-out infinite',
          }}
        />
      </div>
      <div style={{ width: 44, height: 44, background: 'color-mix(in srgb, var(--text) 6%, transparent)', borderRadius: 8, animation: 'ht-skeleton-pulse 1.5s ease-in-out infinite' }} />
      <div style={{ width: 44, height: 44, background: 'color-mix(in srgb, var(--text) 6%, transparent)', borderRadius: 8, animation: 'ht-skeleton-pulse 1.5s ease-in-out infinite' }} />
    </div>
  )
}

function LoadingSkeleton() {
  const { t } = useTranslation('developer')
  return (
    <div aria-busy="true" aria-label={t('apiTokens.loading')} data-testid="loading-skeleton">
      {Array.from({ length: 3 }).map((_, i) => (
        <SkeletonRow key={i} />
      ))}
    </div>
  )
}

// --- Error State ---

function ErrorState({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('developer')
  return (
    <div className="error-state" role="alert" data-testid="error-state">
      <p>{t('apiTokens.error')}</p>
      <button className="btn btn-primary" onClick={onRetry}>
        {t('common:actions.retry')}
      </button>
    </div>
  )
}

// --- Empty State ---

function EmptyState({ onCreateClick }: { onCreateClick: () => void }) {
  const { t } = useTranslation('developer')
  return (
    <div className="empty-state" data-testid="empty-state" style={{ padding: 32, textAlign: 'center' }}>
      <p style={{ marginBottom: 16, color: 'var(--text-subtle)' }}>
        {t('apiTokens.empty')}
      </p>
      <button
        className="btn btn-primary"
        onClick={onCreateClick}
        data-testid="btn-create-empty"
      >
        {t('apiTokens.emptyCreate')}
      </button>
    </div>
  )
}

// --- Toast ---

type ToastMsg = { message: string; type: 'error' | 'success' }

function Toast({ msg, onDismiss }: { msg: ToastMsg; onDismiss: () => void }) {
  const { t } = useTranslation('developer')
  return (
    <div
      role="alert"
      data-testid="toast"
      style={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        background: msg.type === 'error' ? 'var(--danger)' : 'var(--success, #16a34a)',
        color: '#fff',
        padding: '10px 20px',
        borderRadius: 8,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        fontSize: 14,
        boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
        whiteSpace: 'nowrap',
      }}
    >
      {msg.message}
      <button
        onClick={onDismiss}
        style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0 }}
        aria-label={t('common:actions.close')}
      >
        ✕
      </button>
    </div>
  )
}

// --- Rotate-Reveal-Modal ---

type RotateRevealProps = {
  rotatedToken: ApiTokenWithSecret
  onClose: () => void
}

function RotateRevealModal({ rotatedToken, onClose }: RotateRevealProps) {
  const [confirmed, setConfirmed] = useState(false)
  const { t } = useTranslation('developer')

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('rotateRevealModal.ariaLabel')}
      data-testid="rotate-reveal-modal"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 16,
      }}
    >
      <div
        style={{
          background: 'var(--bg)',
          border: '1px solid var(--border)',
          borderRadius: 14,
          padding: '24px',
          width: '100%',
          maxWidth: 480,
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          boxSizing: 'border-box',
        }}
      >
        <h2 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 600 }}>
          {t('rotateRevealModal.title')}
        </h2>
        <p style={{ margin: '0 0 20px', fontSize: 14, color: 'var(--text-subtle)' }}>
          {t('rotateRevealModal.description')} <strong>{rotatedToken.name}</strong>:
        </p>

        <TokenRevealBox token={rotatedToken.token} />

        <div style={{ marginTop: 20, marginBottom: 20 }}>
          <label
            style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 14 }}
          >
            <input
              type="checkbox"
              checked={confirmed}
              onChange={e => setConfirmed(e.target.checked)}
              data-testid="rotate-confirm-checkbox"
              style={{ marginTop: 2, flexShrink: 0, minWidth: 18, minHeight: 18 }}
            />
            <span>{t('rotateRevealModal.confirmCopy')}</span>
          </label>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            className="btn btn-primary"
            onClick={onClose}
            disabled={!confirmed}
            data-testid="rotate-modal-close-btn"
          >
            {t('common:actions.close')}
          </button>
        </div>
      </div>
    </div>
  )
}

// --- Main Component ---

type ApiTokensPageProps = {
  /**
   * Story 5.2: 'embedded' rendert die Token-Verwaltung als Abschnitt im
   * konsolidierten Developer-Portal — ohne eigene Kontingent-Karte (die
   * zeigt das Portal separat), mit h2 statt h1 und div statt main.
   */
  variant?: 'page' | 'embedded'
}

export function ApiTokensPage({ variant = 'page' }: ApiTokensPageProps = {}) {
  const embedded = variant === 'embedded'
  const Root = embedded ? 'div' : 'main'
  const Heading = embedded ? 'h2' : 'h1'
  const {
    tokens,
    isLoading,
    isError,
    isPremiumError,
    refetch,
    createToken,
    rotateToken,
    revokeToken,
    rotatingId,
    revokingId,
  } = useApiTokens()

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [rotatedToken, setRotatedToken] = useState<ApiTokenWithSecret | null>(null)
  const [toast, setToast] = useState<ToastMsg | null>(null)

  function showToast(message: string, type: ToastMsg['type'] = 'error') {
    setToast({ message, type })
    setTimeout(() => setToast(null), 5000)
  }

  async function handleCreateToken(name: string): Promise<string> {
    const result = await createToken(name)
    return result.token
  }

  const { t } = useTranslation('developer')

  async function handleRotate(id: number) {
    try {
      const result = await rotateToken(id)
      setRotatedToken(result)
    } catch {
      showToast(t('apiTokens.toasts.rotateFailed'))
    }
  }

  async function handleRevoke(id: number) {
    try {
      await revokeToken(id)
    } catch {
      showToast(t('apiTokens.toasts.revokeFailed'))
    }
  }

  // R3-S3 (F3.7): Die Kontingent-Karte steht VOR dem Premium-Guard — sonst
  // saehe ein Plan ohne API-Zugang nie, warum er keinen Zugang hat.
  if (isPremiumError) {
    return (
      <Root className="api-tokens-page">
        <Heading style={{ marginBottom: 20 }}>{t('apiTokens.pageTitle')}</Heading>
        {!embedded && <ApiQuotaCard />}
        <PremiumRequired />
      </Root>
    )
  }

  return (
    <Root className="api-tokens-page">
      {!embedded && <ApiQuotaCard />}

      <p
        data-testid="api-tokens-public-api-hint"
        style={{ margin: '0 0 18px', fontSize: 13, color: 'var(--text-subtle)' }}
      >
        {t('apiTokens.publicApiHint')}
      </p>

      <header className="page-header" style={{ display: 'flex', alignItems: 'center', marginBottom: 20, gap: 16 }}>
        <Heading style={{ margin: 0, flex: 1 }}>{t('apiTokens.pageTitle')}</Heading>
        <DemoGuardedButton
          className="btn btn-primary"
          onClick={() => setIsCreateModalOpen(true)}
          data-testid="btn-create-token"
          style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
        >
          <Plus size={16} aria-hidden="true" />
          {t('apiTokens.createBtn')}
        </DemoGuardedButton>
      </header>

      <section aria-label={t('apiTokens.listLabel')}>
        {isLoading && <LoadingSkeleton />}

        {isError && !isLoading && <ErrorState onRetry={refetch} />}

        {!isLoading && !isError && tokens.length === 0 && (
          <EmptyState onCreateClick={() => setIsCreateModalOpen(true)} />
        )}

        {!isLoading && !isError && tokens.length > 0 && (
          <div role="list" aria-label={t('apiTokens.listAriaLabel')} data-testid="tokens-list">
            {tokens.map(token => (
              <ApiTokenRow
                key={token.id}
                token={token}
                isRotating={rotatingId === token.id}
                isRevoking={revokingId === token.id}
                onRotate={(id) => { void handleRotate(id) }}
                onRevoke={(id) => { void handleRevoke(id) }}
              />
            ))}
          </div>
        )}
      </section>

      {/* Create-Modal */}
      <CreateTokenModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreateToken={handleCreateToken}
      />

      {/* Rotate-Reveal-Modal */}
      {rotatedToken && (
        <RotateRevealModal
          rotatedToken={rotatedToken}
          onClose={() => setRotatedToken(null)}
        />
      )}

      {/* Toast */}
      {toast && <Toast msg={toast} onDismiss={() => setToast(null)} />}
    </Root>
  )
}
